import classNames from 'classnames';
import invert from 'invert-color';
import { GlobeIcon } from 'lucide-react';

import { Link } from '@/components/Link/Link';
import { PayloadSlateRenderer } from '@/components/PayloadSlateRenderer/PayloadSlateRenderer';
import { getBackgroundColor } from '@/components/PressReleaseExample/lib';
import { SocialShareBlock } from '@/components/SocialShareBlock/SocialShareBlock';
import { UploadcareImage } from '@/components/UploadcareImage';
import { FEATURE_SITES } from '@/constants';
import { ButtonLink } from '@/src/ui/Button/ButtonLink';
import type { DataAttributes, Example } from '@/types';
import { extractExampleMetadata } from '@/util/extractExampleMetadata';
import { slugify } from '@/util/strings/slugify';

import styles from './PressReleaseExample.module.scss';

interface Props extends DataAttributes {
    example: Example;
    isModal?: boolean;
}

const WHITE_COLOR = '#ffffff';

export function PressReleaseExample({ example, isModal, ...attributes }: Props) {
    const {
        customer,
        title,
        color,
        logo,
        screenshot,
        url,
        categories,
        description,
        type,
        urlStatusCode,
        isExternal,
    } = example;

    const isStory = type !== 'newsroom';
    const isNewsroom = type === 'newsroom';
    const isPageOnline = url && (!urlStatusCode || (urlStatusCode >= 200 && urlStatusCode < 300));

    const meta = extractExampleMetadata(example);

    const backgroundColor = getBackgroundColor(color);
    const shouldUseDarkColors = invert(backgroundColor, true) === WHITE_COLOR;

    return (
        <section
            data-press-release-example={example.id}
            {...attributes}
            className={classNames(styles.section, { [styles.modal]: isModal })}
        >
            <div className={classNames(styles.container, { [styles.modal]: isModal })}>
                <div className={styles.wrapper}>
                    <div className={styles.example}>
                        <div className={styles.info}>
                            <div className={styles.summary}>
                                {logo && (
                                    <UploadcareImage
                                        loading="lazy"
                                        src={logo.url!}
                                        alt={customer ?? 'Company logo'}
                                        className={styles.logo}
                                        width={logo.width}
                                        height={logo.height}
                                    />
                                )}
                                <h1 className={styles.headline} data-press-release-example-title>
                                    {title}
                                </h1>

                                {description && (
                                    <div className={styles.description}>
                                        <PayloadSlateRenderer
                                            data-press-release-example-description
                                            nodes={description}
                                        />
                                    </div>
                                )}

                                {isPageOnline && (
                                    <ButtonLink
                                        size="large"
                                        className={styles.ctaButton}
                                        iconLeft={GlobeIcon}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {isStory && 'Go to Story'}
                                        {isNewsroom && 'Visit site'}
                                    </ButtonLink>
                                )}
                            </div>

                            {!isExternal && categories && categories.length > 0 && (
                                <div className={styles.tags}>
                                    <div className={styles.tag_container}>
                                        <div className={styles.tagTitle}>Industry</div>
                                        <div className={styles.tagsList}>
                                            {categories.map((category) => (
                                                <Link
                                                    data-press-release-example-category={slugify(
                                                        category.title,
                                                    )}
                                                    href={category.path}
                                                    key={category.id}
                                                    className={styles.tag}
                                                    title={category.title}
                                                >
                                                    {category.title}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!isExternal && (
                                <div className={styles.share} data-press-release-example-share>
                                    <div className={styles.shareTitle}>Like it? Share it.</div>
                                    <SocialShareBlock
                                        roundIcons
                                        iconClassName={styles.shareIcon}
                                        url={meta.canonicalUrl}
                                        title={meta.title!}
                                        text={`Check out this great #pressrelease from ${customer} built using @Prezly.`}
                                    />
                                </div>
                            )}
                        </div>

                        <div
                            className={classNames(styles.content, {
                                [styles.darkColors]: shouldUseDarkColors,
                            })}
                            style={{ backgroundColor }}
                        >
                            {isStory && !isExternal && (
                                <div className={styles.tipContainer} data-press-release-example-tip>
                                    <Link
                                        href={'/press-release-examples/create-your-own'}
                                        className="button buttonSmall buttonPrimary"
                                    >
                                        Create your own Press Release with Prezly
                                    </Link>
                                </div>
                            )}

                            {isNewsroom && !isExternal && (
                                <div className={styles.tipContainer} data-press-release-example-tip>
                                    <Link
                                        href={FEATURE_SITES}
                                        className="button buttonSmall buttonPrimary"
                                    >
                                        Set up your own newsroom
                                    </Link>
                                </div>
                            )}

                            {screenshot && (
                                <UploadcareImage
                                    data-press-release-example-screenshot
                                    className={styles.image}
                                    sizes="80vw, (min-width: 768px) 90vw, (min-width: 992px) 55vw, (min-width: 1200px) 65vw, (min-width: 1600px) 1150px"
                                    src={screenshot.url!}
                                    alt={title}
                                    width={screenshot.width}
                                    height={screenshot.height}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
